import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api, toast } from "../App";
import Layout from "@/components/Layout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2 } from "lucide-react";

const DEPARTMENTS = [
  "Management",
  "Sales",
  "Estimation",
  "Projects",
  "PMO",
  "Training",
  "Human Resources",
  "Accounts and Finance",
  "Quality Assurance"
];

const ACCESS_LEVELS = ["Admin", "Management", "Manager", "Team Lead", "Team Member", "Read-only"];

export default function Users({ user }) {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [editingUserId, setEditingUserId] = useState(null);
  
  // Data for dropdowns
  const [employees, setEmployees] = useState([]);
  const [clients, setClients] = useState([]);
  const [interns, setInterns] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [clientContacts, setClientContacts] = useState([]);
  
  const [formData, setFormData] = useState({
    user_type: "",  // Employee, Client, Vendor
    client_id: "",  // Selected client ID (for Client type)
    selected_id: "",  // Employee ID or Client Contact Email
    username: "",
    email: "",
    first_name: "",
    last_name: "",
    password: "",
    department: "Projects",
    access_level: "Team Member",
    employee_id: ""
  });

  // Check if current user has permission to view users
  useEffect(() => {
    if (!user?.permissions?.users?.view) {
      toast.error("You don't have permission to access this page");
      navigate("/dashboard");
    }
  }, [user, navigate]);

  useEffect(() => {
    fetchUsers();
    fetchEmployees();
    fetchClients();
    fetchInterns();
  }, []);

  const fetchEmployees = async () => {
    try {
      const response = await api.get("/employees");
      setEmployees(response.data);
    } catch (error) {
      console.error("Failed to fetch employees:", error);
    }
  };

  const fetchClients = async () => {
    try {
      const response = await api.get("/clients");
      setClients(response.data);
    } catch (error) {
      console.error("Failed to fetch clients:", error);
    }
  };

  const fetchInterns = async () => {
    try {
      const response = await api.get("/interns");
      setInterns(response.data);
    } catch (error) {
      console.error("Failed to fetch interns:", error);
    }
  };

  const handleUserTypeChange = (type) => {
    setFormData({
      ...formData,
      user_type: type,
      client_id: "",
      selected_id: "",
      employee_id: "",  // Reset employee_id when user type changes
      first_name: "",
      last_name: "",
      email: ""
    });
    
    // Reset client selection
    setSelectedClient(null);
    setClientContacts([]);
  };

  const handleClientSelection = async (clientId) => {
    const client = clients.find(c => c.id === clientId);
    setSelectedClient(client);
    setFormData({
      ...formData,
      client_id: clientId,
      selected_id: "",
      first_name: "",
      last_name: "",
      email: ""
    });
    
    // Fetch contacts for selected client from API
    try {
      const response = await api.get(`/contacts?client_id=${clientId}`);
      setClientContacts(response.data.filter((c) => !c.is_left));
    } catch (error) {
      console.error("Failed to fetch contacts for client:", error);
      setClientContacts([]);
    }
  };

  // Helper function to generate username from first and last name
  const generateUsername = (firstName, lastName) => {
    if (!firstName || !lastName) return "";
    // Convert to lowercase, remove special characters, join with dot
    const cleanFirst = firstName.toLowerCase().replace(/[^a-z0-9]/g, '');
    const cleanLast = lastName.toLowerCase().replace(/[^a-z0-9]/g, '');
    return `${cleanFirst}.${cleanLast}`;
  };

  const handleIdSelection = (selectedId) => {
    if (formData.user_type === "Employee" || formData.user_type === "Contract") {
      const employee = employees.find(emp => emp.employee_id === selectedId);
      if (employee) {
        // Use pseudo names if available, otherwise use real names
        const firstName = employee.pseudo_first_name || employee.first_name;
        const lastName = employee.pseudo_last_name || employee.last_name;
        const username = generateUsername(firstName, lastName);
        
        setFormData({
          ...formData,
          selected_id: selectedId,
          employee_id: selectedId,  // CRITICAL: Set the employee_id for backend
          first_name: firstName,
          last_name: lastName,
          username: username,  // Auto-generate username
          email: formData.email,  // Keep existing email if any
          department: employee.department || "Projects",  // Auto-populate from employee
          access_level: employee.access_level || "Team Member",  // Auto-populate from employee
          userType: formData.user_type === "Contract" ? "Contract" : "Employee"
        });
      }
    } else if (formData.user_type === "Interns") {
      const intern = interns.find(int => int.intern_number === selectedId);
      if (intern) {
        // Use pseudo names if available, otherwise use real names
        const firstName = intern.pseudo_first_name || intern.first_name;
        const lastName = intern.pseudo_last_name || intern.last_name;
        const username = generateUsername(firstName, lastName);
        
        setFormData({
          ...formData,
          selected_id: selectedId,
          employee_id: selectedId,  // Store intern_number in employee_id field
          first_name: firstName,
          last_name: lastName,
          username: username,  // Auto-generate username
          email: intern.email || formData.email,  // Use intern email if available
          department: intern.current_department || "Training",  // Auto-populate from intern
          access_level: "Team Member",  // Default access level for interns
          userType: "Intern"  // Set userType for display
        });
      }
    } else if (formData.user_type === "Client") {
      const contact = clientContacts.find(c => c.id === selectedId);
      if (contact) {
        setFormData({
          ...formData,
          selected_id: selectedId,
          employee_id: null,  // Clear employee_id for clients
          first_name: contact.first_name,
          last_name: contact.last_name,
          email: contact.email,
          userType: "Client"  // Set userType for display
        });
      }
    }
  };

  const fetchUsers = async () => {
    try {
      const response = await api.get("/users");
      setUsers(response.data);
    } catch (error) {
      console.error("Failed to fetch users:", error);
      toast.error("Failed to fetch users");
    } finally {
      setLoading(false);
    }
  };

  const handleAccessLevelChange = (level) => {
    setFormData({ ...formData, access_level: level });
  };

  const handleEdit = (userToEdit) => {
    setEditMode(true);
    setEditingUserId(userToEdit.id);
    setFormData({
      username: userToEdit.username,
      email: userToEdit.email,
      first_name: userToEdit.first_name,
      last_name: userToEdit.last_name || "",
      password: "",
      department: userToEdit.department || "Projects",
      access_level: userToEdit.access_level || "Team Member",
      employee_id: userToEdit.employee_id || "none",
      userType: userToEdit.userType || "Employee"
    });
    setDialogOpen(true);
  };

  const handleDelete = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    
    try {
      await api.delete(`/users/${userId}`);
      toast.success("User deleted successfully");
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete user");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const payload = {
        ...formData
      };
      
      // Remove permissions from payload - handled by backend based on access level
      delete payload.permissions;
      
      // Don't send empty employee_id or "none"
      if (!payload.employee_id || payload.employee_id === "none") {
        payload.employee_id = null;
      }

      // Convert empty email to null
      if (!payload.email || payload.email.trim() === "") {
        payload.email = null;
      }

      if (editMode) {
        // Don't send password if empty
        if (!payload.password) {
          delete payload.password;
        }
        // Include userType in update payload if it changed
        await api.put(`/users/${editingUserId}`, payload);
        toast.success("User updated successfully");
      } else {
        // Set userType based on user_type selection for new users
        if (formData.user_type === "Interns") {
          payload.userType = "Intern";
        } else if (formData.user_type === "Employee") {
          payload.userType = "Employee";
        } else if (formData.user_type === "Contract") {
          payload.userType = "Contract";
        } else if (formData.user_type === "Client") {
          payload.userType = "Client";
        } else if (formData.user_type === "Vendor") {
          payload.userType = "Vendor";
        }
        await api.post("/users", payload);
        toast.success("User created successfully");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchUsers();
    } catch (error) {
      // Handle validation errors properly
      let errorMessage = `Failed to ${editMode ? 'update' : 'create'} user`;
      
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        // Check if it's an array of validation errors
        if (Array.isArray(detail)) {
          errorMessage = detail.map(err => err.msg || JSON.stringify(err)).join(', ');
        } else if (typeof detail === 'string') {
          errorMessage = detail;
        } else {
          errorMessage = JSON.stringify(detail);
        }
      }
      
      toast.error(errorMessage);
    }
  };

  const resetForm = () => {
    setEditMode(false);
    setEditingUserId(null);
    setFormData({
      user_type: "",
      client_id: "",
      selected_id: "",
      username: "",
      email: "",
      first_name: "",
      last_name: "",
      password: "",
      department: "Projects",
      access_level: "Team Member",
      employee_id: ""  // Should be empty string, not "none"
    });
    setSelectedClient(null);
    setClientContacts([]);
  };

  const getAccessLevelBadge = (level) => {
    const colors = {
      "Admin": "bg-purple-500",
      "Management": "bg-blue-500",
      "Manager": "bg-green-500",
      "Team Member": "bg-yellow-500",
      "Read-only": "bg-gray-500"
    };
    return colors[level] || "bg-gray-500";
  };

  const getUserTypeBadge = (userType) => {
    const colors = {
      "Employee": "bg-blue-500 text-white",
      "Intern": "bg-orange-500 text-white",
      "Contract": "bg-yellow-600 text-white",
      "Vendor": "bg-teal-500 text-white",
      "Client": "bg-indigo-500 text-white"
    };
    return colors[userType] || "bg-gray-500 text-white";
  };

  return (
    <Layout user={user}>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-md3-display-small">User Management</h1>
            <p className="text-md3-on-surface-variant">Manage users, departments, and access levels</p>
          </div>
          {user?.permissions?.users?.create && (
            <Dialog open={dialogOpen} onOpenChange={(open) => {
              setDialogOpen(open);
              if (!open) resetForm();
            }}>
              <DialogTrigger asChild>
                <Button data-testid="create-user-button" className="bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full">
                  <Plus className="mr-2 h-4 w-4" /> New User
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto bg-white">
                <DialogHeader>
                  <DialogTitle className="text-md3-headline-small text-md3-on-surface font-semibold ">{editMode ? "Edit User" : "Create New User"}</DialogTitle>
                  <DialogDescription className="text-md3-body-medium text-gray-600">
                    {editMode ? "Update user details and permissions" : "Add a new user with department and access level"}
                  </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-6">
                  {/* Basic Information */}
                  <div className="space-y-6">
                    <h3 className="font-semibold text-lg">Basic Information</h3>
                    <div className="grid grid-cols-2 gap-4">
                      {/* User Type Selection - Only for new users */}
                      {!editMode && (
                        <div className="space-y-2">
                          <Label htmlFor="user_type" className="text-md3-label-large text-gray-700 font-medium">Type of User *</Label>
                          <Select
                            value={formData.user_type}
                            onValueChange={handleUserTypeChange}
                          >
                            <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                              <SelectValue placeholder="Select user type" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="Employee">Employee</SelectItem>
                              <SelectItem value="Interns">Interns</SelectItem>
                              <SelectItem value="Contract">Contract</SelectItem>
                              <SelectItem value="Client">Client</SelectItem>
                              <SelectItem value="Vendor">Vendor</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      
                      {/* ID Selection based on User Type - Only for new users */}
                      {/* Employee / Contract ID Selection (uses same employees list) */}
                      {!editMode && (formData.user_type === "Employee" || formData.user_type === "Contract") && (
                        <div className="space-y-2">
                          <Label htmlFor="selected_id" className="text-md3-label-large text-gray-700 font-medium">
                            {formData.user_type === "Contract" ? "Contract Employee ID *" : "Employee ID *"}
                          </Label>
                          <Select
                            value={formData.selected_id}
                            onValueChange={handleIdSelection}
                          >
                            <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20" data-testid={`${formData.user_type.toLowerCase()}-id-select`}>
                              <SelectValue placeholder={formData.user_type === "Contract" ? "Select contract employee" : "Select employee"} />
                            </SelectTrigger>
                            <SelectContent>
                              {employees.map(emp => (
                                <SelectItem key={emp.id} value={emp.employee_id}>
                                  {emp.employee_id} - {emp.first_name} {emp.last_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                          {formData.user_type === "Contract" && (
                            <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded p-2">
                              Contract employees are accrued <strong>2 CL + 2 SL per quarter</strong> and are <strong>not eligible for Earned Leave</strong>.
                            </p>
                          )}
                        </div>
                      )}
                      
                      {/* Intern ID Selection */}
                      {!editMode && formData.user_type === "Interns" && (
                        <div className="space-y-2">
                          <Label htmlFor="selected_id" className="text-md3-label-large text-gray-700 font-medium">Intern ID *</Label>
                          <Select
                            value={formData.selected_id}
                            onValueChange={handleIdSelection}
                          >
                            <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                              <SelectValue placeholder="Select intern" />
                            </SelectTrigger>
                            <SelectContent>
                              {interns.map(intern => (
                                <SelectItem key={intern.id} value={intern.intern_number}>
                                  {intern.intern_number} - {intern.first_name} {intern.last_name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      
                      {/* Client Selection (Level 1) */}
                      {!editMode && formData.user_type === "Client" && (
                        <div className="space-y-2">
                          <Label htmlFor="client_id" className="text-md3-label-large text-gray-700 font-medium">Client *</Label>
                          <Select
                            value={formData.client_id}
                            onValueChange={handleClientSelection}
                          >
                            <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                              <SelectValue placeholder="Select client" />
                            </SelectTrigger>
                            <SelectContent>
                              {clients.map(client => (
                                <SelectItem key={client.id} value={client.id}>
                                  {client.client_number} - {client.client_name || client.name}
                                </SelectItem>
                              ))}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      
                      {/* Contact Selection (Level 2 - only after client selected) */}
                      {!editMode && formData.user_type === "Client" && formData.client_id && (
                        <div className="space-y-2">
                          <Label htmlFor="selected_id" className="text-md3-label-large text-gray-700 font-medium">Contact *</Label>
                          <Select
                            value={formData.selected_id}
                            onValueChange={handleIdSelection}
                          >
                            <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                              <SelectValue placeholder="Select contact" />
                            </SelectTrigger>
                            <SelectContent>
                              {clientContacts.length > 0 ? (
                                clientContacts.map((contact) => (
                                  <SelectItem key={contact.id} value={contact.id}>
                                    {contact.first_name} {contact.last_name} ({contact.email})
                                  </SelectItem>
                                ))
                              ) : (
                                <SelectItem value="no_contacts" disabled>No contacts available</SelectItem>
                              )}
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      
                      {/* Vendor Selection */}
                      {!editMode && formData.user_type === "Vendor" && (
                        <div className="space-y-2">
                          <Label htmlFor="selected_id" className="text-md3-label-large text-gray-700 font-medium">Vendor ID *</Label>
                          <Select
                            value={formData.selected_id}
                            onValueChange={handleIdSelection}
                          >
                            <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                              <SelectValue placeholder="Select vendor" />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="vendor_placeholder" disabled>No vendors available</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      )}
                      
                      <div className="space-y-2">
                        <Label htmlFor="username" className="text-md3-label-large text-gray-700 font-medium">Username *</Label>
                        <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="username"
                          value={formData.username}
                          onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                          required
                          disabled={editMode}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="email" className="text-md3-label-large text-gray-700 font-medium">Email</Label>
                        <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="email"
                          type="email"
                          value={formData.email}
                          onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                          readOnly={!editMode && formData.user_type === "Client" && formData.selected_id}
                          className={!editMode && formData.user_type === "Client" && formData.selected_id ? "bg-md3-surface-container" : ""}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="first_name" className="text-md3-label-large text-gray-700 font-medium">First Name *</Label>
                        <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="first_name"
                          value={formData.first_name}
                          onChange={(e) => {
                            const newFirstName = e.target.value;
                            const newUsername = generateUsername(newFirstName, formData.last_name);
                            setFormData({ ...formData, first_name: newFirstName, username: newUsername });
                          }}
                          required
                          readOnly={!editMode && formData.selected_id}
                          className={!editMode && formData.selected_id ? "bg-md3-surface-container" : ""}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="last_name" className="text-md3-label-large text-gray-700 font-medium">Last Name</Label>
                        <Input className={`bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary ${!editMode && formData.selected_id ? "bg-md3-surface-container" : ""}`} id="last_name" value={formData.last_name} readOnly={!editMode && formData.selected_id} onChange={(e) => {
                            const newLastName = e.target.value;
                            const newUsername = generateUsername(formData.first_name, newLastName);
                            setFormData({ ...formData, last_name: newLastName, username: newUsername });
                          }}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="password" className="text-md3-label-large text-gray-700 font-medium">Password {!editMode && "*"}</Label>
                        <Input className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20 focus:border-md3-primary" id="password"
                          type="password"
                          value={formData.password}
                          onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                          required={!editMode}
                          placeholder={editMode ? "Leave empty to keep current" : ""}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Department and Access Level */}
                  <div className="space-y-6">
                    <h3 className="font-semibold text-lg">Department & Access Level</h3>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="department" className="text-md3-label-large text-gray-700 font-medium">Department *</Label>
                        <Select value={formData.department} onValueChange={(value) => setFormData({ ...formData, department: value })}>
                          <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {DEPARTMENTS.map(dept => (
                              <SelectItem key={dept} value={dept}>{dept}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="access_level" className="text-md3-label-large text-gray-700 font-medium">Access Level *</Label>
                        <Select value={formData.access_level} onValueChange={handleAccessLevelChange}>
                          <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent>
                            {ACCESS_LEVELS.map(level => (
                              <SelectItem key={level} value={level}>{level}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  </div>

                  {/* User Type - shown in edit mode for Intern/Employee/Contract users */}
                  {editMode && ["Intern", "Employee", "Contract"].includes(formData.userType) && (
                    <div className="space-y-6">
                      <h3 className="font-semibold text-lg">User Type Conversion</h3>
                      <div className={`p-3 border rounded ${formData.userType === "Intern" ? "bg-orange-50 border-orange-200" : formData.userType === "Contract" ? "bg-yellow-50 border-yellow-200" : "bg-blue-50 border-blue-200"}`}>
                        <p className="text-md3-body-small mb-3" style={{color: formData.userType === "Intern" ? "#92400e" : formData.userType === "Contract" ? "#78350f" : "#1e3a5f"}}>
                          {formData.userType === "Intern" && <><strong>Note:</strong> This user is an Intern. Convert to Employee once they complete their internship.</>}
                          {formData.userType === "Contract" && <><strong>Note:</strong> This user is a Contract employee. Admins can convert to Employee or back.</>}
                          {formData.userType === "Employee" && <><strong>Note:</strong> Admins can convert this employee to Contract employment.</>}
                        </p>
                        <div className="space-y-2">
                          <Label htmlFor="userType" className="text-md3-label-large text-gray-700 font-medium">User Type</Label>
                          <Select
                            value={formData.userType}
                            onValueChange={(value) => setFormData({ ...formData, userType: value })}
                            disabled={user?.access_level !== "Admin" && formData.userType === "Employee"}
                          >
                            <SelectTrigger className="bg-white border-gray-300 focus:border-md3-primary focus:ring-2 focus:ring-md3-primary/20">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {formData.userType === "Intern" && <SelectItem value="Intern">Intern</SelectItem>}
                              <SelectItem value="Employee">Employee</SelectItem>
                              <SelectItem value="Contract">Contract</SelectItem>
                            </SelectContent>
                          </Select>
                          {formData.userType !== "Contract" && (
                            <p className="text-xs text-gray-500">Note: Converting to Contract will zero out Earned Leave balance.</p>
                          )}
                        </div>
                      </div>
                    </div>
                  )}

                  <p className="text-md3-body-small text-md3-on-surface-variant p-3 bg-blue-50 border border-blue-200 rounded">
                    <strong>Note:</strong> Permissions are automatically set based on Department and Access Level. 
                    To customize permissions, use the <strong>Permission Matrix</strong> in Settings.
                  </p>

                  <Button type="submit" className="w-full bg-md3-primary hover:bg-md3-primary/90 text-md3-on-primary shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full" >
                    {editMode ? "Update User" : "Create User"}
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          )}
        </div>

        <Card className="bg-md3-surface-container shadow-md3-1 hover:shadow-md3-2 transition-shadow duration-200 rounded-md3-lg border-0">
          <CardHeader>
            <CardTitle className="text-md3-title-large text-md3-on-surface ">All Users</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <p className="text-md3-on-surface-variant">Loading...</p>
            ) : users.length === 0 ? (
              <p className="text-md3-on-surface-variant">No users yet</p>
            ) : (
              <div className="overflow-x-auto">
                <Table className="bg-md3-surface-container rounded-md3-md">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Username</TableHead>
                      <TableHead>Name</TableHead>
                      <TableHead>Email</TableHead>
                      <TableHead>User Type</TableHead>
                      <TableHead>Department</TableHead>
                      <TableHead>Access Level</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((userItem) => (
                      <TableRow key={userItem.id}>
                        <TableCell className="font-medium">{userItem.username}</TableCell>
                        <TableCell>{userItem.first_name} {userItem.last_name || ''}</TableCell>
                        <TableCell>{userItem.email}</TableCell>
                        <TableCell>
                          <Badge className={getUserTypeBadge(userItem.userType)}>
                            {userItem.userType || 'Employee'}
                          </Badge>
                        </TableCell>
                        <TableCell>{userItem.department || '-'}</TableCell>
                        <TableCell>
                          <Badge className={getAccessLevelBadge(userItem.access_level)}>
                            {userItem.access_level || 'Team Member'}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex gap-2">
                            {user?.permissions?.users?.edit && (
                              <Button size="sm"
                                variant="outline"
                                className="border-md3-outline hover:bg-md3-surface-container-high transition-all duration-200 rounded-md3-full"
                                onClick={() => handleEdit(userItem)}>
                                <Pencil className="h-4 w-4" />
                              </Button>
                            )}
                            {user?.permissions?.users?.delete && userItem.id !== user.id && (
                              <Button size="sm"
                                variant="destructive"
                                className="bg-red-600 hover:bg-red-700 text-white shadow-md3-1 hover:shadow-md3-2 transition-all duration-200 rounded-md3-full"
                                onClick={() => handleDelete(userItem.id)}>
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
}
